import React, { useState, useEffect } from "react";
import { Plus, Search, ArrowUpDown, MoreVertical } from "lucide-react";
import { useSelector } from "react-redux";
import DashboardLayout from "../../components/DashboardLayout";
import AddVehicleModal from "./AddVehicleModal";
import PlanUsage, { PLAN_FULL_TITLE, usePlanUsage } from "../../components/PlanUsage";
import AssignDriverModal from "./AssignDriverModal";
import VehicleDetailsPage from "./VehicleDetailsPage";
import { useDeleteFleetMutation, useGetAllBranchesQuery, useGetAllFleetsQuery } from "../../redux/services/fleetManagement";
import { RootState } from "../../redux/services/store";
import { toast } from "sonner";
import DeleteFleetModal from "./DeleteFleetModal";
import { usePermission } from "../../utils/permissions";

export type TripLog = {
  date: string;
  tripStarts: string;
  tripEnds: string;
  distance: string;
  duration: string;
  status: "In-Transit" | "Completed" | "Cancelled";
};

export type MaintenanceRecord = {
  datePerformed: string;
  type: string;
  cost: string;
};

export type Vehicle = {
  id: string;
  fleet_id: string;
  make: string;
  model: string;
  year: string;
  licensePlate: string;
  vin: string;
  capacity: string;
  assignedDriver: string;
  userId: string;
  deliveryLocation: string;
  status: string;
  date: string;
  branch: string;
  type: string;
  lastLocation: string;
  lastUpdated: string;
  avgRating: string;
  completedTrips: number;
  tripLogs: TripLog[];
  maintenanceHistory: MaintenanceRecord[];
};

const statusStyles: Record<string, string> = {
  Assigned: "bg-green-100 text-green-700",
  Pending: "bg-yellow-100 text-yellow-700",
  "Un-Assigned": "bg-orange-100 text-orange-700",
};

export default function FleetManagementPage() {
  const { can } = usePermission();
  const canAddFleet = can("add fleet");
  const canEditFleet = can("edit fleet");
  const canDeleteFleet = can("delete fleet");
  const user = useSelector((state: RootState) => state.auth.user);
  const companyId = user?.company?.id ?? "";
  const [currentPage, setCurrentPage] = useState(1);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const { isFull: fleetFull } = usePlanUsage("fleets");
  const [showAssignDriver, setShowAssignDriver] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Vehicle | null>(null);

  const [deleteFleet, { isLoading: deleting }] = useDeleteFleetMutation();

  const { data: fleetsData, isLoading: fleetsLoading, refetch: refetchFleets } = useGetAllFleetsQuery(
    { company_id: companyId, page: currentPage },
    { skip: !companyId }
  );
  const { data: branchesData, isLoading: branchesLoading } = useGetAllBranchesQuery(
    { user_id: user?.id ?? "" },
    { skip: !user?.id }
  );

  const branchData = branchesData?.data?.branchInfo?.data;

  const pageSize = fleetsData?.data?.fleets?.per_page ?? 10;
  const totalPages = fleetsData?.data?.fleets?.last_page ?? 1;
  const totalFleets = fleetsData?.data?.fleets?.total ?? vehicles.length;

  // Update vehicles when API data changes
  useEffect(() => {
    if (fleetsData?.data?.fleets?.data) {
      const apiVehicles: Vehicle[] = fleetsData.data.fleets.data.map((fleet: any) => ({
        id: fleet.id || `#${Math.floor(10000 + Math.random() * 90000)}`,
        fleet_id: fleet.id || `#${Math.floor(10000 + Math.random() * 90000)}`,
        model: fleet.model_id || "Not Specified",
        year: "2026",
        licensePlate: fleet.license_plate || "Not Specified",
        vin: fleet.vin || "Not Specified",
        capacity: fleet.capacity?.toString() || "Not Specified",
        assignedDriver: fleet.user ? fleet.user.name || "Unassigned" : "Unassigned",
        userId: fleet.user_id || "",
        deliveryLocation: fleet.branch?.branch_name || "Not Specified",
        status: fleet.status === "pending" ? "Pending" : fleet.status,
        date: new Date(fleet.created_at || Date.now()).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
        branch: fleet.branch?.location || "Not Specified",
        type: fleet.vehicle_type || "Not Specified",
        lastLocation: fleet.branch?.location || "Not Specified",
        lastUpdated: fleet.updated_at ? new Date(fleet.updated_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "Just now",
        avgRating: "N/A",
        completedTrips: 0,
        tripLogs: [],
        maintenanceHistory: [],
      }));
      setVehicles(apiVehicles);
    }
  }, [fleetsData]);

  const summary = {
    total: fleetsData?.data?.totalFleet ?? vehicles.length,
    active: vehicles.filter((v) => v.status === "Assigned").length,
    maintenance: vehicles.filter((v) => v.status === "In-Maintenance").length,
    idle: fleetsData?.data?.inactiveFleet ?? vehicles.filter((v) => v.status === "Pending").length,
  };

  const filtered = vehicles.filter((v) => {
    const matchSearch = `${v.make} ${v.model} ${v.id}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus ? v.status === filterStatus : true;
    const matchLocation = filterLocation ? v.deliveryLocation === filterLocation : true;
    return matchSearch && matchStatus && matchLocation;
  });

  const paginatedVehicles = filtered;
  const showingStart = filtered.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingEnd = (currentPage - 1) * pageSize + filtered.length;

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterStatus, filterLocation]);

  const handleAddVehicle = async (data: Partial<Vehicle>) => {
    // The API call is handled in the modal, so we just need to refetch the data
    // RTK Query will automatically invalidate and refetch due to the invalidatesTags
    await refetchFleets();
    setShowAddVehicle(false);
  };

  const confirmDeleteVehicle = async () => {
    if (!deleteTarget) return;
    if (!canDeleteFleet) {
      toast.error("You don't have permission to delete fleet");
      return;
    }

    try {
      await deleteFleet({
        fleet_id: deleteTarget.fleet_id,
      }).unwrap();

      setVehicles((prev) =>
        prev.filter(
          (vehicle) =>
            vehicle.fleet_id !== deleteTarget.fleet_id
        )
      );

      await refetchFleets();

      toast.success("Vehicle deleted successfully");

      setDeleteTarget(null);
      setOpenMenuId(null);
    } catch (error) {
      toast.error("Failed to delete vehicle");
    }
  };

  if (selectedVehicle) {
    return (
      <VehicleDetailsPage
        vehicle={selectedVehicle}
        onBack={() => setSelectedVehicle(null)}
        onAssignDriver={() => setShowAssignDriver(true)}
      />
    );
  }

  return (
    <DashboardLayout
      title="Fleet Management"
      description="Get a clear, actionable view of your staff and operations in one place. Track key metrics, and export the data you need to make faster, smarter decisions every day."
    >
      {fleetsLoading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading fleet data...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Action buttons row */}
          <div className="flex flex-wrap items-center justify-end gap-3 my-6 lg:mt-0 px-4 lg:px-0 w-full">
            <PlanUsage resource="fleets" />
            {/* <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-500 bg-white">
              <span>14/03/2025</span><span>—</span><span>14/04/2025</span>
              <RefreshCw size={13} className="text-gray-400" />
            </div> */}
            {/* <button
              onClick={() => setShowAssignDriver(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors hover:bg-blue-50"
              style={{ borderColor: "#143887", color: "#143887" }}
            >
              <UserPlus size={16} /> Assign Driver
            </button> */}
            <button
              onClick={() => setShowAddVehicle(true)}
              disabled={!canAddFleet || fleetFull}
              title={!canAddFleet ? "You don't have permission to add fleet" : fleetFull ? PLAN_FULL_TITLE : undefined}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "#143887" }}
            >
              <Plus size={16} /> Add New Vehicle
            </button>
          </div>

          {/* Summary Cards */}
          <div className="w-full mb-6 px-4 lg:px-0">
            <p className="text-sm font-semibold text-gray-700 mb-3">Summary</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {[
                { label: "Total vehicles", value: summary.total, change: "2% from previous month", up: true },
                { label: "Assigned vehicles", value: summary.active, change: "4% from last quarter", up: false },
                // { label: "Vehicles in maintenance", value: summary.maintenance, change: "8% Up from last quarter", up: true },
                // { label: "Idle vehicles", value: summary.idle, change: "8% Up from last quarter", up: true },
              ].map((card, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                  <p className="text-sm text-gray-500 mb-2">{card.label}</p>
                  <p className="text-3xl font-bold mb-2" style={{ color: "#143887" }}>{card.value}</p>
                  <p className={`text-xs ${card.up ? "text-green-600" : "text-red-500"}`}>
                    {card.up ? "↑" : "↓"} {card.change}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Fleet Table */}
          <div className="w-full bg-white rounded-xl border border-gray-100 shadow-sm mb-8 px-4 lg:px-0">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between py-4 lg:px-4 border-b border-gray-100 flex-wrap gap-3">
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search Model..."
                  className="pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-full focus:outline-none focus:border-blue-300 w-full lg:w-72"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">Filter By:</span>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border border-gray-200 rounded-full px-2 py-2 text-xs focus:outline-none text-gray-600 flex-1"
                >
                  <option value="">Status</option>
                  {["Assigned", "Pending", "Un-Assigned"].map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
                <select
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                  className="border border-gray-200 rounded-full px-2 py-2 text-xs focus:outline-none text-gray-600 flex-1"
                >
                  <option value="">{branchesLoading ? "Loading locations..." : "Delivery location"}</option>
                  {branchData?.map((l: any) =>{
                    const locationStr = l?.branch_detail?.branch_name || '';
                    return (
                      <option key={locationStr} value={locationStr}>
                        {locationStr}
                      </option>
                    )
                  })}
                </select>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto border border-gray-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-500 text-xs">
                    {["Make & Model", "License plate", "Assigned driver", "Delivery location", "Status", "Date", "Actions"].map((col) => (
                      <th key={col} className="text-left px-4 py-3 font-medium">
                        <span className="flex items-center gap-1">{col} <ArrowUpDown size={11} /></span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedVehicles.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-10 text-gray-400 text-sm">
                        <div className="flex flex-col items-center justify-center gap-2 py-10">
                          <div className="text-5xl text-gray-300">📄</div>
                          <p className="text-base font-medium text-gray-600">No vehicles found.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedVehicles.map((v, i) => (
                      <tr
                        key={i}
                        onClick={() => setSelectedVehicle(v)}
                        className="border-b border-gray-50 hover:bg-blue-50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 font-medium text-gray-800">{v.model}</td>
                        <td className="px-4 py-3 text-gray-600">{v.licensePlate}</td>
                        <td className="px-4 py-3 text-gray-700">{v.assignedDriver}</td>
                        <td className="px-4 py-3 text-gray-600">{v.deliveryLocation}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[v.status]}`}>
                            {v.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{v.date}</td>
                        <td
                          className="px-4 py-3 text-gray-500 text-xs relative"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() =>
                              setOpenMenuId(openMenuId === v.fleet_id ? null : v.fleet_id)
                            }
                            className="p-2 hover:bg-gray-100 rounded-lg"
                          >
                            <MoreVertical size={15} className="text-gray-400" />
                          </button>

                          {openMenuId === v.fleet_id && (
                            <div className="absolute right-4 top-10 bg-white border border-gray-200 rounded-lg shadow-lg w-36 z-50">
                              <button
                                onClick={() => {
                                  if (!canEditFleet) {
                                    toast.error("You don't have permission to edit fleet");
                                    return;
                                  }
                                  setSelectedVehicle(v); // opens details page
                                  setOpenMenuId(null);
                                }}
                                disabled={!canEditFleet}
                                title={!canEditFleet ? "You don't have permission to edit fleet" : undefined}
                                className="w-full text-left px-4 py-2 hover:bg-gray-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Update
                              </button>

                              <button
                                onClick={() => {
                                  if (!canDeleteFleet) {
                                    toast.error("You don't have permission to delete fleet");
                                    return;
                                  }
                                  setDeleteTarget(v);
                                  setOpenMenuId(null);
                                }}
                                disabled={!canDeleteFleet}
                                title={!canDeleteFleet ? "You don't have permission to delete fleet" : undefined}
                                className="w-full text-left px-4 py-2 hover:bg-red-50 text-red-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="px-4 py-3 border-t border-gray-300">
              <div className="flex items-center justify-between">
                <p className=" text-gray-600">
                  Showing {showingStart}-{showingEnd} of {totalFleets}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="p-2 hover:bg-gray-100 rounded disabled:bg-gray-300 border border-gray-300 disabled:cursor-not-allowed"
                    title="First page"
                  >
                    <svg className="w-3 h-3 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 hover:bg-gray-100 rounded disabled:bg-gray-300 border border-gray-300 disabled:cursor-not-allowed"
                    title="Previous page"
                  >
                    <svg className="w-3 h-3 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 hover:bg-gray-100 rounded disabled:bg-gray-300 border border-gray-300 disabled:cursor-not-allowed"
                    title="Next page"
                  >
                    <svg className="w-3 h-3 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-2 hover:bg-gray-100 rounded disabled:bg-gray-300 border border-gray-300 disabled:cursor-not-allowed"
                    title="Last page"
                  >
                    <svg className="w-3 h-3 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {showAddVehicle && (
        <AddVehicleModal
          onClose={() => setShowAddVehicle(false)}
          onSubmit={handleAddVehicle}
          vehicles={vehicles}
        />
      )}
      {showAssignDriver && (
        <AssignDriverModal
          vehicles={vehicles}
          onClose={() => setShowAssignDriver(false)}
        />
      )}
      {deleteTarget && (
        <DeleteFleetModal
          vehicle={deleteTarget}
          deleting={deleting}
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDeleteVehicle}
        />
      )}

    </DashboardLayout>
  );

  // return (
  //   <DashboardLayout
  //     title="Fleet Management"
  //     description="Get a clear, actionable view of your staff and operations in one place. Track key metrics, and export the data you need to make faster, smarter decisions every day."
  //   >
  //     {fleetsLoading ? (
  //       <div className="flex items-center justify-center min-h-[400px] px-4">
  //         <div className="text-center">
  //           <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
  //           <p className="text-gray-600 text-sm sm:text-base">
  //             Loading fleet data...
  //           </p>
  //         </div>
  //       </div>
  //     ) : (
  //       <>
  //         {/* Top Action */}
  //         <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 mb-6 w-full">
  //           <button
  //             onClick={() => setShowAddVehicle(true)}
  //             className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-white"
  //             style={{ background: "#143887" }}
  //           >
  //             <Plus size={16} />
  //             Add New Vehicle
  //           </button>
  //         </div>

  //         {/* Summary */}
  //         <div className="w-full mb-6">
  //           <p className="text-sm font-semibold text-gray-700 mb-3">Summary</p>

  //           <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
  //             {[
  //               {
  //                 label: "Total vehicles",
  //                 value: summary.total,
  //                 change: "2% from previous month",
  //                 up: true,
  //               },
  //               {
  //                 label: "Active vehicles",
  //                 value: summary.active,
  //                 change: "4% from last quarter",
  //                 up: false,
  //               },
  //             ].map((card, i) => (
  //               <div
  //                 key={i}
  //                 className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm"
  //               >
  //                 <p className="text-sm text-gray-500 mb-2">{card.label}</p>
  //                 <p
  //                   className="text-2xl sm:text-3xl font-bold mb-2"
  //                   style={{ color: "#143887" }}
  //                 >
  //                   {card.value}
  //                 </p>

  //                 <p
  //                   className={`text-xs ${
  //                     card.up ? "text-green-600" : "text-red-500"
  //                   }`}
  //                 >
  //                   {card.up ? "↑" : "↓"} {card.change}
  //                 </p>
  //               </div>
  //             ))}
  //           </div>
  //         </div>

  //         {/* Table/Card Section */}
  //         <div className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm mb-8 overflow-hidden">
  //           {/* Toolbar */}
  //           <div className="p-4 border-b border-gray-100 space-y-3">
  //             <p className="font-semibold text-gray-800 text-sm sm:text-base">
  //               Fleet Management Table
  //             </p>

  //             <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
  //               {/* Search */}
  //               <div className="relative md:col-span-2">
  //                 <Search
  //                   size={14}
  //                   className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
  //                 />
  //                 <input
  //                   value={search}
  //                   onChange={(e) => setSearch(e.target.value)}
  //                   placeholder="Search Make, Model or ID"
  //                   className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none"
  //                 />
  //               </div>

  //               {/* Status */}
  //               <select
  //                 value={filterStatus}
  //                 onChange={(e) => setFilterStatus(e.target.value)}
  //                 className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
  //               >
  //                 <option value="">All Status</option>
  //                 {["Assigned", "Pending", "Un-Assigned"].map((s) => (
  //                   <option key={s}>{s}</option>
  //                 ))}
  //               </select>

  //               {/* Location */}
  //               <select
  //                 value={filterLocation}
  //                 onChange={(e) => setFilterLocation(e.target.value)}
  //                 className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
  //               >
  //                 <option value="">All Locations</option>
  //                 {locationOptions.map((l) => (
  //                   <option key={l}>{l}</option>
  //                 ))}
  //               </select>
  //             </div>
  //           </div>

  //           {/* MOBILE CARDS */}
  //           <div className="block lg:hidden divide-y">
  //             {paginatedVehicles.length === 0 ? (
  //               <div className="p-8 text-center text-gray-400 text-sm">
  //                 No vehicles found
  //               </div>
  //             ) : (
  //               paginatedVehicles.map((v) => (
  //                 <div
  //                   key={v.fleet_id}
  //                   onClick={() => setSelectedVehicle(v)}
  //                   className="p-4 space-y-3 hover:bg-gray-50 cursor-pointer"
  //                 >
  //                   <div className="flex justify-between items-start gap-3">
  //                     <div>
  //                       <p className="font-semibold text-gray-800">{v.model}</p>
  //                       <p className="text-xs text-gray-500">
  //                         {v.licensePlate}
  //                       </p>
  //                     </div>

  //                     <button
  //                       onClick={(e) => {
  //                         e.stopPropagation();
  //                         setOpenMenuId(
  //                           openMenuId === v.fleet_id ? null : v.fleet_id
  //                         );
  //                       }}
  //                       className="p-2 rounded-lg hover:bg-gray-100"
  //                     >
  //                       <MoreVertical size={16} />
  //                     </button>
  //                   </div>

  //                   <div className="grid grid-cols-2 gap-3 text-xs">
  //                     <div>
  //                       <p className="text-gray-400">Driver</p>
  //                       <p>{v.assignedDriver}</p>
  //                     </div>

  //                     <div>
  //                       <p className="text-gray-400">Location</p>
  //                       <p>{v.deliveryLocation}</p>
  //                     </div>

  //                     <div>
  //                       <p className="text-gray-400">Date</p>
  //                       <p>{v.date}</p>
  //                     </div>

  //                     <div>
  //                       <p className="text-gray-400">Status</p>
  //                       <span
  //                         className={`px-2 py-1 rounded-full text-xs font-medium inline-block ${statusStyles[v.status]}`}
  //                       >
  //                         {v.status}
  //                       </span>
  //                     </div>
  //                   </div>

  //                   {openMenuId === v.fleet_id && (
  //                     <div className="border rounded-xl overflow-hidden">
  //                       <button
  //                         onClick={(e) => {
  //                           e.stopPropagation();
  //                           setSelectedVehicle(v);
  //                           setOpenMenuId(null);
  //                         }}
  //                         className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50"
  //                       >
  //                         Update
  //                       </button>

  //                       <button
  //                         onClick={(e) => {
  //                           e.stopPropagation();
  //                           setDeleteTarget(v);
  //                           setOpenMenuId(null);
  //                         }}
  //                         className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50"
  //                       >
  //                         Delete
  //                       </button>
  //                     </div>
  //                   )}
  //                 </div>
  //               ))
  //             )}
  //           </div>

  //           {/* DESKTOP TABLE */}
  //           <div className="hidden lg:block overflow-x-auto">
  //             <table className="w-full text-sm min-w-[950px]">
  //               <thead>
  //                 <tr className="border-b border-gray-100 text-gray-500 text-xs">
  //                   {[
  //                     "Model",
  //                     "Plate",
  //                     "Driver",
  //                     "Location",
  //                     "Status",
  //                     "Date",
  //                     "Actions",
  //                   ].map((col) => (
  //                     <th key={col} className="text-left px-4 py-3 font-medium">
  //                       {col}
  //                     </th>
  //                   ))}
  //                 </tr>
  //               </thead>

  //               <tbody>
  //                 {paginatedVehicles.map((v) => (
  //                   <tr
  //                     key={v.fleet_id}
  //                     onClick={() => setSelectedVehicle(v)}
  //                     className="border-b hover:bg-blue-50 cursor-pointer"
  //                   >
  //                     <td className="px-4 py-4">{v.model}</td>
  //                     <td className="px-4 py-4">{v.licensePlate}</td>
  //                     <td className="px-4 py-4">{v.assignedDriver}</td>
  //                     <td className="px-4 py-4">{v.deliveryLocation}</td>

  //                     <td className="px-4 py-4">
  //                       <span
  //                         className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[v.status]}`}
  //                       >
  //                         {v.status}
  //                       </span>
  //                     </td>

  //                     <td className="px-4 py-4">{v.date}</td>

  //                     <td
  //                       className="px-4 py-4 relative"
  //                       onClick={(e) => e.stopPropagation()}
  //                     >
  //                       <button
  //                         onClick={() =>
  //                           setOpenMenuId(
  //                             openMenuId === v.fleet_id
  //                               ? null
  //                               : v.fleet_id
  //                           )
  //                         }
  //                         className="p-2 rounded-lg hover:bg-gray-100"
  //                       >
  //                         <MoreVertical size={15} />
  //                       </button>

  //                       {openMenuId === v.fleet_id && (
  //                         <div className="absolute right-4 top-12 bg-white border rounded-xl shadow-lg w-36 z-50">
  //                           <button
  //                             onClick={() => {
  //                               setSelectedVehicle(v);
  //                               setOpenMenuId(null);
  //                             }}
  //                             className="w-full px-4 py-2 text-left hover:bg-gray-50"
  //                           >
  //                             Update
  //                           </button>

  //                           <button
  //                             onClick={() => {
  //                               setDeleteTarget(v);
  //                               setOpenMenuId(null);
  //                             }}
  //                             className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50"
  //                           >
  //                             Delete
  //                           </button>
  //                         </div>
  //                       )}
  //                     </td>
  //                   </tr>
  //                 ))}
  //               </tbody>
  //             </table>
  //           </div>

  //           {/* Pagination */}
  //           <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
  //             <p className="text-sm text-gray-500">
  //               Showing {showingStart}-{showingEnd} of {totalFleets}
  //             </p>

  //             <div className="flex flex-wrap gap-2">
  //               <button
  //                 onClick={() => setCurrentPage(currentPage - 1)}
  //                 disabled={currentPage === 1}
  //                 className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40"
  //               >
  //                 Prev
  //               </button>

  //               <button
  //                 onClick={() => setCurrentPage(currentPage + 1)}
  //                 disabled={currentPage === totalPages}
  //                 className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40"
  //               >
  //                 Next
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </>
  //     )}

  //     {/* MODALS */}
  //     {showAddVehicle && (
  //       <AddVehicleModal
  //         onClose={() => setShowAddVehicle(false)}
  //         onSubmit={handleAddVehicle}
  //         vehicles={vehicles}
  //       />
  //     )}

  //     {showAssignDriver && (
  //       <AssignDriverModal
  //         vehicles={vehicles}
  //         onClose={() => setShowAssignDriver(false)}
  //       />
  //     )}

  //     {deleteTarget && (
  //       <DeleteFleetModal
  //         vehicle={deleteTarget}
  //         deleting={deleting}
  //         onClose={() => setDeleteTarget(null)}
  //         onConfirm={confirmDeleteVehicle}
  //       />
  //     )}
  //   </DashboardLayout>
  // );
}